import React, { useEffect, useState } from 'react';
import { inventoryApi } from '../services/api';

interface LowStockProduct {
  product_id: string;
  variant_id: string;
  product_title: string;
  variant_title: string;
  sku: string;
  inventory_quantity: number;
  image_url: string | null;
  shopify_inventory_item_id: string | null;
  shopify_variant_id: string | null;
}

interface InventoryAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InventoryAlertModal: React.FC<InventoryAlertModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [threshold, setThreshold] = useState(10);
  const [adjustingId, setAdjustingId] = useState<string | null>(null);
  const [adjustAmounts, setAdjustAmounts] = useState<Record<string, string>>({});
  const [adjustSuccess, setAdjustSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadLowStockProducts();
    }
  }, [isOpen]);

  const loadLowStockProducts = async () => {
    setLoading(true);
    try {
      const response = await inventoryApi.getLowStockProducts();
      setLowStockProducts(response.low_stock_products || []);
      setThreshold(response.settings?.low_stock_threshold || 10);
    } catch (error) {
      console.error('Failed to load low stock products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustInventory = async (variantId: string) => {
    const amount = parseInt(adjustAmounts[variantId] || '0');
    if (!amount || amount === 0) return;

    setAdjustingId(variantId);
    try {
      const result = await inventoryApi.adjustInventory(variantId, amount);

      // Update the local state with the new quantity
      setLowStockProducts(prev =>
        prev.map(p =>
          p.variant_id === variantId
            ? { ...p, inventory_quantity: result.new_quantity }
            : p
        ).filter(p => p.inventory_quantity <= threshold)
      );

      // Clear the input and show success
      setAdjustAmounts(prev => ({ ...prev, [variantId]: '' }));
      setAdjustSuccess(variantId);
      setTimeout(() => setAdjustSuccess(null), 2000);
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to adjust inventory';
      alert(message);
    } finally {
      setAdjustingId(null);
    }
  };

  const getSeverityColor = (quantity: number) => {
    if (quantity === 0) {
      return 'text-red-700 bg-red-100';
    } else if (quantity <= Math.floor(threshold / 3)) {
      return 'text-orange-700 bg-orange-100';
    }
    return 'text-yellow-700 bg-yellow-100';
  };

  const getStockLabel = (quantity: number) => {
    if (quantity === 0) return 'Out of stock';
    return `${quantity} left`;
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 safe-px safe-pt safe-pb">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Low Stock Alerts
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Products with inventory at or below {threshold} units
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-slate-600">Loading...</div>
                  </div>
                ) : lowStockProducts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <svg
                      className="w-16 h-16 text-green-500 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-lg font-medium text-slate-900">All Stocked Up</p>
                    <p className="text-sm text-slate-600 mt-1">
                      All products are above the low stock threshold
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                    {lowStockProducts.map((product) => (
                      <div
                        key={product.variant_id}
                        className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {/* Product image */}
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.product_title}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                          )}

                          <div className="min-w-0">
                            <div className="flex items-center gap-3">
                              <span className="font-semibold text-slate-900 truncate">
                                {product.product_title}
                              </span>
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${getSeverityColor(
                                  product.inventory_quantity
                                )}`}
                              >
                                {getStockLabel(product.inventory_quantity)}
                              </span>
                            </div>
                            <div className="mt-1 text-sm text-slate-600">
                              {product.variant_title !== 'Default Title' && (
                                <span>{product.variant_title} &bull; </span>
                              )}
                              {product.sku && <span>SKU: {product.sku}</span>}
                            </div>
                          </div>
                        </div>

                        {/* Adjust inventory */}
                        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                          {adjustSuccess === product.variant_id ? (
                            <span className="text-green-600 text-sm font-medium flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Updated
                            </span>
                          ) : product.shopify_inventory_item_id ? (
                            <>
                              <input
                                type="number"
                                placeholder="Qty"
                                min="1"
                                value={adjustAmounts[product.variant_id] || ''}
                                onChange={(e) =>
                                  setAdjustAmounts(prev => ({
                                    ...prev,
                                    [product.variant_id]: e.target.value,
                                  }))
                                }
                                className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue text-center"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleAdjustInventory(product.variant_id);
                                  }
                                }}
                              />
                              <button
                                onClick={() => handleAdjustInventory(product.variant_id)}
                                disabled={
                                  adjustingId === product.variant_id ||
                                  !adjustAmounts[product.variant_id] ||
                                  parseInt(adjustAmounts[product.variant_id]) === 0
                                }
                                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {adjustingId === product.variant_id ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    ...
                                  </span>
                                ) : (
                                  '+ Add'
                                )}
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              Sync needed
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
                <div className="text-sm text-slate-600">
                  Low Stock Threshold: {threshold} units &bull; {lowStockProducts.length} product{lowStockProducts.length !== 1 ? 's' : ''} below threshold
                </div>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
